import { Component, HostBinding, Input, OnChanges, OnInit } from '@angular/core';

/**
 * Skeleton de carregamento: a forma do conteudo antes de o dado chegar.
 *
 * Substitui o overlay de tela cheia do ngx-spinner nas CARGAS (abrir a tela,
 * filtrar). Nas ACOES (salvar, excluir, importar) o spinner continua: um
 * skeleton nao representa "estou gravando". A decisao e do projeto
 * P-HUB-143, e a receita de uso esta em docs/skeletons.md.
 *
 * Cinco formas, escolhidas por `tipo`:
 *   linhas   N linhas de texto, com larguras variadas para parecer texto
 *   tabela   cabecalho + N linhas x M colunas (a forma de quase toda lista)
 *   form     N pares rotulo + campo (a forma das telas de edicao)
 *   cartoes  N cartoes de indicador, lado a lado (os KPIs dos relatorios)
 *   campo    um osso da altura de um .form-control, no lugar de um select
 *            cujas opcoes ainda vem da API (filtros em cascata)
 *
 * Por padrao o desenho vem dentro de um .card.card-default, que e onde toda
 * tela do SIG poe o seu conteudo; `cartao=false` entrega so os ossos, para
 * quem ja esta dentro de um cartao. O `campo` nunca vem num cartao.
 *
 * Cores e animacao sao so CSS, por classe. Nada passa por [style.*]: o
 * ViewEngine do Angular 8 sanitiza esse binding e descarta var() (HUB-9852).
 * Na impressao o componente some pela regra global de styles.scss, que vale
 * para o window.print e para as folhas injetadas na pagina. A janela do
 * ngx-print nao leva CSS nenhum (nenhuma tela usa useExistingCss); la a
 * garantia e outra: o skeleton e o dado nunca estao no DOM ao mesmo tempo.
 */
@Component({
  selector: 'app-skeleton',
  templateUrl: './skeleton.component.html',
  styleUrls: ['./skeleton.component.scss']
})
export class SkeletonComponent implements OnInit, OnChanges {
  /* Larguras das linhas de texto (sk-l1..sk-l5 no SCSS). Sao fixas e repetem
     em ciclo para o desenho ser igual a cada render: aleatorio piscaria a
     cada troca de filtro. */
  private static readonly LARGURAS = 5;

  // Por @HostBinding e nao por `host:` no decorator, que o tslint do projeto proibe.
  @HostBinding('attr.role') readonly papel = 'status';
  @HostBinding('attr.aria-busy') readonly ocupado = 'true';
  @HostBinding('attr.aria-live') readonly aviso = 'polite';

  @Input() tipo: 'linhas' | 'tabela' | 'form' | 'cartoes' | 'campo' = 'linhas';
  /** linhas de texto, ou linhas da tabela */
  @Input() linhas = 5;
  /** colunas da tabela: use o numero de <th> do quadro real */
  @Input() colunas = 6;
  /** pares rotulo + campo do formulario */
  @Input() campos = 4;
  /** cartoes de indicador */
  @Input() cartoes = 4;
  /** envolve o desenho num .card.card-default */
  @Input() cartao = true;
  /** texto para leitor de tela; a forma em si e aria-hidden. Vazio: sem texto. */
  @Input() rotulo = 'Carregando…';

  seqLinhas: number[] = [];
  seqColunas: number[] = [];
  seqCampos: number[] = [];
  seqCartoes: number[] = [];

  private static seq(n: number): number[] {
    const total = Math.max(0, Math.min(60, Math.floor(+n || 0)));
    const out: number[] = [];
    for (let i = 0; i < total; i++) { out.push(i); }
    return out;
  }

  // O ngOnInit cobre o <app-skeleton> sem nenhum input, que nunca recebe ngOnChanges.
  ngOnInit(): void {
    this.monta();
  }

  ngOnChanges(): void {
    this.monta();
  }

  /** Classe de largura da linha `i` (sk-l1..sk-l5), ciclica. */
  larguraClasse(i: number): string {
    return 'sk-l' + ((i % SkeletonComponent.LARGURAS) + 1);
  }

  private monta(): void {
    this.seqLinhas = SkeletonComponent.seq(this.linhas);
    this.seqColunas = SkeletonComponent.seq(this.colunas);
    this.seqCampos = SkeletonComponent.seq(this.campos);
    this.seqCartoes = SkeletonComponent.seq(this.cartoes);
  }
}
